import { Button, ButtonTheme } from 'components/Button';
import { HeaderToolbar } from 'components/HeaderToolbar';
import { ICONS } from 'components/Icon';
import { IconButton } from 'components/IconButton';
import { ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { Menu } from 'components/Menu';
import { Txt } from 'components/Txt';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { SidebarShowButton } from 'componentsAware/SidebarShowButton';
import { useSelector } from 'hooks/useSelector';
import { pluralise } from 'lib/pluralise';
import { filterArrayEmpty, randomKey } from 'lib/riskbase';
import { theme } from 'lib/theme';
import * as React from 'react';
import { View } from 'react-native';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL } from 'store/actions';
import {
  doEngageFlatsLoad,
  doEngageFlatsSetFilter,
} from 'store/remote/engageActions';
import {
  selectEngageFlatsFilterSearch,
  selectEngageFlatsFilterOccupationStatuses,
  selectEngageFlatsFilterIssueContactTypes,
  selectEngageFlatsFilterIsCommerciallyOwned,
  selectEngageFlatsFilterIsHMO,
  selectEngageFlatsRequiresResidentSupport,
} from 'store/remote/engageSelectors';
import { EngageFlatsFilter, EngagePropertyListItem } from 'types/Engage';
import { Highlight } from 'components/Highlight';
import { BlockManagerPropertyFeatureEnums } from 'types/DTOs/BlockManagerDTOs';
import { useScreenSize } from 'hooks/useScreenSize';
import { ScreenSize } from 'lib/constants';
type Props = {
  isLoading: boolean;
  property: EngagePropertyListItem;
  onBackPress: () => void;
  onReloadPress: () => void;
  onOnboardFlatsPress: () => void;
  onExportCsvPress: () => void;
  onScheduledVisitsPress: () => void;
  flatsCount: number;
  pageKey: string;
  filtersLocked?: boolean;
  hasBulkSelection?: boolean;
  onBulkSendPress?: () => void;
};

export const EngageFlatsHeader: React.FC<Props> = ({
  isLoading,
  property,
  onBackPress,
  onReloadPress,
  onOnboardFlatsPress,
  onExportCsvPress,
  onScheduledVisitsPress,
  flatsCount,
  pageKey,
  filtersLocked = false,
  hasBulkSelection = false,
  onBulkSendPress,
}) => {
  const dispatch = useDispatch();
  const screenSize = useScreenSize();

  const searchValue = useSelector((state) =>
    selectEngageFlatsFilterSearch(state, pageKey),
  );
  const selectedOccupationStatuses = useSelector((state) =>
    selectEngageFlatsFilterOccupationStatuses(state, pageKey),
  );
  const contactIssueTypeStatus = useSelector((state) =>
    selectEngageFlatsFilterIssueContactTypes(state, pageKey),
  );
  const requiresResidentSupportStatus = useSelector((state) =>
    selectEngageFlatsRequiresResidentSupport(state, pageKey),
  );
  const isHMOFilterStatus = useSelector((state) =>
    selectEngageFlatsFilterIsHMO(state, pageKey),
  );
  const isCommerciallyOwnedFilterStatus = useSelector((state) =>
    selectEngageFlatsFilterIsCommerciallyOwned(state, pageKey),
  );

  const searchValueRef = React.useRef(searchValue);
  const textfieldKeyRef = React.useRef(randomKey());
  if (searchValueRef.current !== searchValue) {
    textfieldKeyRef.current = randomKey();
    searchValueRef.current = searchValue;
  }

  const handleEngageFilter = React.useCallback(
    (filterChanges: Partial<EngageFlatsFilter>) => {
      if (filtersLocked) {
        return;
      }
      dispatch(
        doEngageFlatsSetFilter({
          key: pageKey,
          data: filterChanges,
        }),
      );
      dispatch(
        doEngageFlatsLoad({
          key: pageKey,
          propertyId: property.id,
          page: 'reset',
        }),
      );
    },
    [dispatch, filtersLocked, pageKey, property.id],
  );

  const handleSearch = React.useCallback(
    (newSearchValue: string) => {
      searchValueRef.current = newSearchValue;
      handleEngageFilter({ search: newSearchValue });
    },
    [handleEngageFilter],
  );

  const filterIsTriggered =
    (searchValue?.trim()?.length ?? 0) > 0 ||
    (selectedOccupationStatuses?.length ?? 0) > 0 ||
    (contactIssueTypeStatus?.length ?? 0) > 0 ||
    (requiresResidentSupportStatus?.length ?? 0) > 0 ||
    (isHMOFilterStatus?.length ?? 0) > 0 ||
    (isCommerciallyOwnedFilterStatus?.length ?? 0) > 0;

  const handleOpenFiltersModal = React.useCallback(() => {
    if (filtersLocked) {
      return;
    }
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.EngageFlatsFilter,
        size: 'small',
        params: {
          pageKey,
          propertyId: property.id,
        },
      }),
    );
  }, [dispatch, filtersLocked, pageKey, property.id]);

  return (
    <View>
      <NavigationHeader marginRight={0} marginLeft={0}>
        <SidebarShowButton />
        <ViewWithSeparators
          horizontal
          style={{
            alignItems: 'center',
            flex: 1,
          }}
          separatorMargin={theme.paddingHorizontalHalf}
          separatorColor="transparent"
        >
          <Button
            icon={ICONS.ChevronLeftLight}
            text="Engage"
            buttonTheme={ButtonTheme.header}
            onPress={onBackPress}
            style={{
              paddingLeft: theme.paddingHorizontalHalf,
              paddingRight: theme.paddingHorizontal,
            }}
            chipLike
          />
          <Txt weight="bold" numberOfLines={1} style={{ flex: 1 }}>
            {property.name}{' '}
            <Txt
              numberOfLines={1}
              style={{ color: theme.colors.fieldValueText }}
            >
              {isLoading
                ? 'Loading Flats'
                : pluralise(flatsCount, '{x} Flat', '{x} Flats', 'No Flats')}
            </Txt>
          </Txt>
          {property.features.includes(
            BlockManagerPropertyFeatureEnums.scheduledvisits,
          ) &&
            screenSize === ScreenSize.large && (
              <Button
                text="Scheduled Visits"
                onPress={onScheduledVisitsPress}
                buttonTheme={ButtonTheme.themeless}
              />
            )}
          <Menu
            iconButtonConfig={{ icon: ICONS.EllipsisLight }}
            options={[
              property.features.includes(
                BlockManagerPropertyFeatureEnums.importflatcontacts,
              )
                ? {
                    text: 'Import Contacts',
                    subText: 'Upload a CSV of contacts',
                    icon: ICONS.FileImportLight,
                    onPress: onExportCsvPress,
                  }
                : null,
              '-',
              property.features.includes(
                BlockManagerPropertyFeatureEnums.scheduledvisits,
              ) && screenSize !== ScreenSize.large
                ? {
                    text: 'Scheduled Visits',
                    subText: 'Manage scheduled visits',
                    onPress: onScheduledVisitsPress,
                  }
                : null,

              '-',
              {
                text: 'Onboard Flats',
                subText: 'Onboard new flats into engage',
                onPress: onOnboardFlatsPress,
              },
              '-',
              {
                text: 'Reload',
                icon: ICONS.RotateRightLight,
                onPress: onReloadPress,
              },
            ].filter(filterArrayEmpty)}
          />
        </ViewWithSeparators>
      </NavigationHeader>
      <HeaderToolbar
        key={textfieldKeyRef.current}
        searchValue={searchValue}
        onSearch={handleSearch}
        searchPlaceholder="Search flats and contacts"
        searchDisabled={filtersLocked}
        showSearch={!hasBulkSelection}
        leftChildren={
          filtersLocked && hasBulkSelection && onBulkSendPress ? (
            <View style={{ alignItems: 'flex-start' }}>
              <Highlight highlight style={{ borderRadius: 99 }}>
                <Button
                  icon={ICONS.PaperPlaneLight}
                  text="Send Message"
                  onPress={onBulkSendPress}
                  chipLike
                  buttonTheme={ButtonTheme.themeless}
                />
              </Highlight>
            </View>
          ) : undefined
        }
        leftChildrenWithBox
      >
        <IconButton
          icon={ICONS.SlidersUpLight}
          color={
            filtersLocked ? theme.colors.fieldValueText : theme.colors.blue
          }
          onPress={handleOpenFiltersModal}
          showBadge={filterIsTriggered}
          disabled={filtersLocked}
        />
      </HeaderToolbar>
    </View>
  );
};
